import { DynaVault } from '../../../api/DynaVaultsAPI';
import { ChartsSection } from 'structure/capital-manager/components/ChartsSection';
import { Header3 } from 'structure/home/components/Typography';
import React, { useMemo } from 'react';
import { Body1, Body2 } from 'components/Typography';
import { TokenImage } from 'components/TokenImage';
import { getNetworkIconUrlByChaiId, getTokenIconUrl } from 'utils/url';
import {
  filterIdleTokens,
  filterThirdPartyTokens,
} from 'structure/capital-manager/utils/tokens';
import { getNetworkByChainId } from '@dapp/multi-lib/network';
import { LegacyPositionsSection } from 'structure/capital-manager/components/LegacyPositionsSection';
import { TokenAction, TokenTableRow } from 'structure/capital-manager/types';
import { formatUnits } from 'viem';
import { ZapNow } from 'structure/capital-manager/components/ZapNow';
import { findVaultListByDepositToken } from 'structure/capital-manager/utils/vaults';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import Link from 'next/link';
import { formatUsd } from '@dapp/multi-lib/lib/formatters';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';

export interface ContentProps {
  tokens: TokenTableRow[];
  vaultList: DynaVault[];
  handleClickZap: (params: {
    depositTokenAddress: `0x${string}`;
    vault: DynaVault;
  }) => void;
}

export const DesktopContent = (props: ContentProps) => {
  const { tokens, vaultList, handleClickZap } = props;

  const idleTokens = useMemo(() => {
    return filterIdleTokens(tokens);
  }, [tokens]);

  const thirdPartyTokens = useMemo(() => {
    return filterThirdPartyTokens(tokens);
  }, [tokens]);

  return (
    <div className='flex flex-col gap-6'>
      <ChartsSection vaultList={vaultList} tokens={tokens} />

      {idleTokens.length > 0 ? (
        <>
          <Header3>Idle Tokens</Header3>
          <TokenTable
            rows={idleTokens}
            vaultList={vaultList}
            handleClickZap={handleClickZap}
          />
        </>
      ) : null}

      {thirdPartyTokens.length > 0 ? (
        <>
          <Header3>Third party positions</Header3>
          <TokenTable
            rows={thirdPartyTokens}
            vaultList={vaultList}
            handleClickZap={handleClickZap}
          />
        </>
      ) : null}
      <LegacyPositionsSection />
    </div>
  );
};

interface TokenTableProps {
  rows: TokenTableRow[];
  vaultList: DynaVault[];
  handleClickZap: (params: {
    depositTokenAddress: `0x${string}`;
    vault: DynaVault;
  }) => void;
}

const TokenTable = (props: TokenTableProps) => {
  const { rows, vaultList, handleClickZap } = props;
  return (
    <div>
      <div className='flex flex-row gap-2'>
        <div className='basis-1/12'>
          <Body2 className='text-[#B5B5B5]'>Chain</Body2>
        </div>
        <div className='basis-4/12'>
          <Body2 className='text-[#B5B5B5]'>Token</Body2>
        </div>
        <div className='basis-3/12'>
          <Body2 className='text-[#B5B5B5]'>Balance</Body2>
        </div>
        <div className='basis-4/12'>
          <Body2 className='text-[#B5B5B5]'>Actions</Body2>
        </div>
      </div>
      <div>
        {rows.map((token) => (
          <div key={token.id} className='flex items-center gap-2 py-4'>
            <div className='flex basis-1/12 flex-row items-center gap-2'>
              <div className='flex min-h-[32px] min-w-[32px]'>
                <TokenImage
                  symbol={getNetworkByChainId(token.chainId)?.shortName ?? ''}
                  src={getNetworkIconUrlByChaiId(token.chainId)}
                  alt={getNetworkByChainId(token.chainId)?.shortName ?? ''}
                  width={32}
                  height={32}
                />
              </div>
              <Body1 className='text-[1rem]'>
                {getNetworkByChainId(token.chainId)?.shortName}
              </Body1>
            </div>
            <div className='flex basis-4/12 flex-row items-center gap-3'>
              <div className='relative flex min-h-[32px] min-w-[32px]'>
                <TokenImage
                  symbol={token.symbol}
                  src={getTokenIconUrl(token.symbol)}
                  height='32'
                  width='32'
                  alt={token.symbol}
                />
                {token.tokenAction === TokenAction.BuySFI && (
                  <div className='absolute -bottom-[12px] -right-[12px] flex h-[24px] w-[24px] items-center justify-center rounded-full bg-[#2B2B2B] p-1'>
                    <Icon icon='gas' height={24} width={24} color='#B4BCD0' />
                  </div>
                )}
              </div>
              <Body1 className='text-[1rem] font-bold'>{token.name}</Body1>
            </div>
            <div className='flex basis-3/12 flex-col'>
              <Body1 className='text-[1rem]'>
                {formatUnits(token.balance, token.decimals)} {token.symbol}
              </Body1>
              <Body1 className='text-[1rem] font-bold'>
                ${formatUsd(token.balanceUsd)}
              </Body1>
            </div>
            <div className='basis-4/12'>
              {token.tokenAction === TokenAction.Zap && (
                <ZapNow
                  depositTokenAddress={token.address}
                  vaultList={findVaultListByDepositToken(
                    vaultList,
                    token.address
                  )}
                  onZap={handleClickZap}
                />
              )}
              {token.tokenAction === TokenAction.BuySFI && (
                <Button
                  component={Link}
                  className='inline-flex shrink-0 items-center gap-2 text-[1.125rem] lg:text-[1rem]'
                  href={token.buySFITokenHref}
                  target='_blank'
                >
                  Buy SFI
                </Button>
              )}

              {token.tokenAction === TokenAction.Stake && (
                <Button
                  component={Link}
                  className='inline-flex shrink-0 items-center gap-2 text-[1.125rem] lg:text-[1rem]'
                  href={`/staking/${token.symbol}`}
                  target='_blank'
                >
                  Stake {token.symbol}
                </Button>
              )}

              {token.tokenAction === TokenAction.Migrate && (
                <Button
                  component={Link}
                  className='inline-flex shrink-0 items-center gap-2 text-[1.125rem] lg:text-[1rem]'
                  href={`/migrate`}
                  target='_blank'
                >
                  Migrate {token.symbol}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
